import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { formatClassNames } from "../../../../utils/common";
import Chip from "../../../atoms/chip";
import PropTypes from "prop-types";
import { setClickedFilterChip, setIsFilterOpen, setIsSortOpen } from "../../../../store/slices/plpSlice";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { plpPageConstants } from "../../../../constants/pageConstants/plpConstants";

/**
 * PLP Top Filters, Sort Chips component.
 * @param {*} param0
 * @returns
 */
const FilterChips = ({ hasFacetsList = false, chipData = [] }) => {
  const dispatch = useDispatch();
  const filterContainer = useRef(null);

  const { appliedFilters, appliedSort, sortOptions } = useSelector(
    (state) => ({
      appliedFilters: state?.plp?.appliedFilters,
      appliedSort: state?.plp?.appliedSort,
      sortOptions: state?.plp?.details?.sortOptions,
    }),
    shallowEqual,
  );

  const initialData = useMemo(
    () => [
      ...(hasFacetsList
        ? [
            {
              id: plpPageConstants.FILTER_ID,
              name: plpPageConstants.FILTER,
              startIcon: "/icons/funnel.svg",
              isActive: false,
              count: 0,
            },
          ]
        : []),
      {
        id: plpPageConstants.SORT_BY_ID,
        name: plpPageConstants.SORT_BY,
        startIcon: "/icons/sort.svg",
        isActive: false,
      },
    ],
    [hasFacetsList],
  );

  const filteredChipData = useMemo(() => {
    if (!Array.isArray(chipData)) return [];
    return chipData.map((item) => ({
      id: item?.facetFieldName,
      name: item?.facetFieldDisplayName,
      isActive: false,
      count: 0,
    }));
  }, [chipData]);

  const baseData = useMemo(() => [...initialData, ...filteredChipData], [initialData, filteredChipData]);

  const mergeFiltersData = useMemo(() => {
    let data = baseData.map((item) => ({ ...item }));

    // Sorting Logic
    if (appliedSort?.sortBy) {
      const matchingItem = sortOptions?.find(
        (option) => option?.sortBy === appliedSort?.sortBy && option?.sortOrder === appliedSort?.sortOrder,
      );
      data = data.map((item) =>
        item.id === plpPageConstants.SORT_BY_ID
          ? {
              ...item,
              name: matchingItem?.level || item.name,
              isActive: true,
            }
          : item,
      );
    }

    // Filters Logic
    const validApplied = (appliedFilters || []).filter(Boolean);
    const ids = validApplied.map((f) => f.split(":")[0]);

    data = data.map((item) => {
      if (item.id === plpPageConstants.FILTER_ID) {
        return {
          ...item,
          isActive: validApplied.length > 0,
          count: validApplied.length,
        };
      }

      if (item.id === plpPageConstants.SORT_BY_ID) return item;

      const active = ids.includes(item.id);
      return { ...item, isActive: active, count: active ? item.count : 0 };
    });

    return data;
  }, [baseData, appliedFilters, appliedSort, sortOptions]);

  useEffect(() => {
    filterContainer.current?.scrollTo({ left: 0, behavior: "smooth" });
  }, [mergeFiltersData]);

  const displayData = useMemo(() => {
    const pinnedIds = [plpPageConstants.FILTER_ID, plpPageConstants.SORT_BY_ID];
    const pinned = pinnedIds.map((id) => mergeFiltersData.find((i) => i?.id === id)).filter(Boolean);

    const restIds = mergeFiltersData.filter((i) => !pinnedIds.includes(i?.id));

    const sortedRest = [...restIds].sort((a, b) => {
      if (a.isActive === b.isActive) return 0;
      return a.isActive ? -1 : 1;
    });

    filterContainer?.current?.scrollTo(0, 0);
    return [...pinned, ...sortedRest];
  }, [mergeFiltersData]);

  const displayKey = useMemo(() => displayData.map((i) => i?.id).join("|"), [displayData]);

  /**
   * Chip Click Handler
   */
  const onChipClickHandler = useCallback(
    (id) => {
      if (id === plpPageConstants.SORT_BY_ID) {
        dispatch(setIsSortOpen(true));
      } else {
        dispatch(setClickedFilterChip(id));
        dispatch(setIsFilterOpen(true));
      }
    },
    [dispatch],
  );

  return (
    <div
      ref={filterContainer}
      id="plpFilterChips"
      className="sticky left-0 top-[48px] z-1 -mx-16 flex gap-8 overflow-x-auto border-thinLightGrey bg-white-900 px-16 pb-10 pt-12 scrollbar-none sm:-mx-40"
    >
      {displayData.map((category, idx) => {
        const isPinned = category.id === plpPageConstants.FILTER_ID || category.id === plpPageConstants.SORT_BY_ID;

        const chipClasses = formatClassNames(
          "cursor-pointer border rounded h-36 text-14 font-400 leading-5 py-8 capitalize gap-3",
          "transition-transform duration-200 ease-out",
          !isPinned && "animate-slide-in-from-right",
          category.isActive
            ? "bg-fnp-100 text-fnp-400 border-darkOliveGreen"
            : "bg-white-900 border-greySoft text-grey-500 hover:border-grey",
          idx <= 1 ? "px-24" : "px-14",
        );

        return (
          <Chip
            key={`${displayKey}-${category.id}`}
            id={category.id}
            classes={chipClasses}
            startIcon={category.startIcon}
            endIcon="/icons/arrow-down-filled.svg"
            onClick={() => onChipClickHandler(category.id)}
            size="medium"
            color="default"
            variant="default"
            iconSize={16}
            content={
              <div>
                {category.name?.toLowerCase()}
                {category.count > 0 && (
                  <span className="relative -top-1 ml-4 inline-block h-16 w-16 rounded-full bg-fnp-300 text-center text-10 font-500 leading-4 text-white-900">
                    {category.count}
                  </span>
                )}
              </div>
            }
          />
        );
      })}
    </div>
  );
};

FilterChips.propTypes = {
  chipData: PropTypes.array,
  hasFacetsList: PropTypes.bool,
};

export default FilterChips;
